import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaFingerprint } from "react-icons/fa";
import { TbFingerprintOff } from "react-icons/tb";

interface Props {
  storedHash: string;
}

const MAX_UPLOAD_SIZE_MB = 20;
const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * 1024 * 1024;
const PDF_MAGIC = "%PDF-";

type CheckState =
  | { kind: "idle" }
  | { kind: "computing"; fileName: string }
  | { kind: "match"; fileName: string }
  | { kind: "mismatch"; fileName: string }
  | { kind: "error"; message: string };

/**
 * Opt-in document integrity check. The user uploads the PDF they're holding
 * and we compare its SHA-256 locally against the hash recorded on the server
 * when the document was signed.
 *
 * The file never leaves the user's device — `crypto.subtle.digest` hashes
 * the ArrayBuffer in-browser and we only compare two hex strings.
 */
const DocumentIntegrityCheck: React.FC<Props> = ({ storedHash }) => {
  const { t } = useTranslation("clientView");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [state, setState] = useState<CheckState>({ kind: "idle" });

  const normalizedStored = (storedHash || "").trim().toLowerCase();

  const toHex = (buffer: ArrayBuffer) =>
    Array.from(new Uint8Array(buffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

  const handleFile = async (file: File | null) => {
    if (!file) return;

    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      setState({
        kind: "error",
        message: String(
          t("integrity.error.tooLarge", { max: MAX_UPLOAD_SIZE_MB })
        ),
      });
      return;
    }

    setState({ kind: "computing", fileName: file.name });

    try {
      const buffer = await file.arrayBuffer();

      const header = new TextDecoder("ascii").decode(
        new Uint8Array(buffer, 0, Math.min(5, buffer.byteLength))
      );
      if (header !== PDF_MAGIC) {
        setState({
          kind: "error",
          message: String(t("integrity.error.notPdf")),
        });
        return;
      }

      const digest = await crypto.subtle.digest("SHA-256", buffer);
      const localHash = toHex(digest);

      if (localHash === normalizedStored) {
        setState({ kind: "match", fileName: file.name });
      } else {
        setState({ kind: "mismatch", fileName: file.name });
      }
    } catch (err) {
      setState({
        kind: "error",
        message:
          err instanceof Error
            ? err.message
            : String(t("integrity.error.generic")),
      });
    }
  };

  const reset = () => {
    setState({ kind: "idle" });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <section className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-6 mb-5">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
          <FaFingerprint className="text-blue-500" size={18} />
        </div>
        <h2 className="text-base font-semibold text-gray-800">
          {t("integrity.title")}
        </h2>
      </div>

      <p className="text-sm text-gray-500 leading-relaxed mb-4">
        {t("integrity.description")}
      </p>

      {state.kind === "idle" && (
        <label className="flex flex-col items-center justify-center gap-2 w-full border-2 border-dashed border-gray-300 rounded-xl p-6 cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors">
          <FaFingerprint className="text-gray-400" size={32} />
          <span className="text-sm text-gray-600 font-medium">
            {t("integrity.uploadCta")}
          </span>
          <span className="text-xs text-gray-400 text-center">
            {t("integrity.uploadHint")}
          </span>
          <span className="text-xs text-gray-400">
            {t("integrity.uploadMaxSize", { max: MAX_UPLOAD_SIZE_MB })}
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="sr-only"
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
          />
        </label>
      )}

      {state.kind === "computing" && (
        <div className="flex flex-col items-center justify-center gap-2 w-full border-2 border-dashed border-blue-300 bg-blue-50/40 rounded-xl p-6">
          <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-600">
            {t("integrity.computing")}
          </span>
          <span className="text-xs text-gray-400 break-all">
            {state.fileName}
          </span>
        </div>
      )}

      {state.kind === "match" && (
        <div className="rounded-xl ring-1 ring-green-200 bg-green-50 p-6 text-center">
          <FaFingerprint className="mx-auto text-green-500" size={56} />
          <p className="text-base font-semibold text-green-800 mt-3">
            {t("integrity.match.title")}
          </p>
          <p className="text-sm text-green-700 mt-1.5 leading-relaxed">
            {t("integrity.match.body")}
          </p>
        </div>
      )}

      {state.kind === "mismatch" && (
        <div className="rounded-xl ring-1 ring-red-200 bg-red-50 p-6 text-center">
          <TbFingerprintOff className="mx-auto text-red-500" size={56} />
          <p className="text-base font-semibold text-red-800 mt-3">
            {t("integrity.mismatch.title")}
          </p>
          <p className="text-sm text-red-700 mt-1.5 leading-relaxed">
            {t("integrity.mismatch.body")}
          </p>
          <p className="text-xs text-red-700/70 mt-3 break-all">
            {state.fileName}
          </p>
          <div className="mt-4">
            <button
              type="button"
              onClick={reset}
              className="text-xs text-red-700 hover:text-red-800 font-medium"
            >
              {t("integrity.checkAnother")}
            </button>
          </div>
        </div>
      )}

      {state.kind === "error" && (
        <div className="rounded-xl ring-1 ring-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-800">
            {t("integrity.error.title")}
          </p>
          <p className="text-xs text-amber-700 mt-1 leading-relaxed">
            {state.message}
          </p>
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={reset}
              className="text-xs text-amber-700 hover:text-amber-800 font-medium"
            >
              {t("integrity.retry")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default DocumentIntegrityCheck;
